import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyDomainKey,
  ShopifyDomainToMarketWebPresencesKey,
  ShopifyDomainToShopKey,
  ShopifyMarketWebPresenceKey,
  ShopifyMarketWebPresenceToDomainKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyDomainKey,
  "2022-01": {
    name: "Shopify Domain",
    fields: [
      {
        name: "Host",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "host",
            shopifyFieldKey: "host",
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
          },
        },
      },
      {
        name: "Localization",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "localization",
            shopifyFieldKey: "localization",
          },
        },
      },
      {
        name: "SSL Enabled",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "sslEnabled",
            shopifyFieldKey: "ssl_enabled",
          },
        },
      },
      {
        name: "URL",
        fieldType: FieldType.URL,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "url",
            shopifyFieldKey: "url",
          },
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDomainToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "shop_id",
            graphQLFieldKey: "shop",
          },
        },
      },
    ],
    syncConfig: {
      type: "custom",
      resource: "domain",
    },
  },
  "2022-04": {
    fields: [
      {
        name: "Market Web Presences",
        fieldType: FieldType.HasMany,
        key: ShopifyDomainToMarketWebPresencesKey,
        configuration: {
          relatedModelKey: ShopifyMarketWebPresenceKey,
          inverseFieldKey: ShopifyMarketWebPresenceToDomainKey,
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Host",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "host",
            shopifyFieldKey: "host",
            availableInWebhook: ["domains/create", "domains/update"],
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
            availableInWebhook: ["domains/create", "domains/update"],
          },
        },
      },
      {
        name: "Localization",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "localization",
            shopifyFieldKey: "localization",
            availableInWebhook: ["domains/create", "domains/update"],
          },
        },
      },
      {
        name: "SSL Enabled",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "sslEnabled",
            shopifyFieldKey: "ssl_enabled",
            availableInWebhook: ["domains/create", "domains/update"],
          },
        },
      },
    ],
  },
};

export const ShopifyDomain = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
